"use client"

import { cn } from "@/lib/utils"
import { useTema } from "./tema"

/*
  ─── A marca ───

  Dois arquivos, um por tema, porque a arte tem cor própria: no claro o logo é
  tinta escura, e no escuro ele sai TODO na cor do acento.

  No escuro o arquivo do designer é usado como MÁSCARA, e não desenhado como
  imagem: a arte tinha duas cores (sete caminhos no lime antigo `#D7F205` e oito
  em branco), e o lime dela nem era mais o da marca. Como máscara, o recorte é o
  do designer e a cor é o token, então ela acompanha quem trocou a cor em
  "Customizar o app" e não há hex congelado dentro do SVG.

  O **símbolo** vem inline, e não de arquivo: ele aparece na navegação recolhida,
  onde precisa acompanhar a cor do tema, e três caminhos em `currentColor`
  resolvem isso sem um terceiro e quarto arquivo para manter em dia.
*/

/** A proporção de cada arquivo (largura dividida por altura). */
const PROPORCAO_ESCURO = 814 / 108

/**
 * O logo inteiro (símbolo e nome), na versão do tema.
 *
 * **No escuro ele sai TODO na cor do acento**, e não em lime e branco como a
 * arte original. A arte tinha sete caminhos no lime antigo (`#D7F205`, do tema
 * v1) e oito em branco: era duas cores, e uma delas nem era mais a cor da marca.
 *
 * Ele é pintado por MÁSCARA, e não por `<img>`: o arquivo do designer vira o
 * recorte e a cor vem do token. Três coisas vêm de graça com isso:
 *
 * - o lime é sempre o **atual** (`--d-acento`), e não um hex congelado no SVG;
 * - quem trocou a cor em "Customizar o app" vê a marca na cor dele;
 * - não existe um terceiro arquivo para manter em dia quando a arte mudar.
 *
 * No claro continua o `<img>` do arquivo: lá a arte é tinta escura de uma cor
 * só e não há o que recolorir.
 */
export function LogoWelltrainer({
  altura = 24,
  className,
}: {
  /** Em pixels. A largura acompanha a proporção do arquivo. */
  altura?: number
  className?: string
}) {
  const tema = useTema()

  if (tema === "escuro") {
    const mascara = "url(/marca/welltrainer-escuro.svg)"
    return (
      <span
        role="img"
        aria-label="Welltrainer"
        className={cn("block select-none", className)}
        style={{
          height: altura,
          width: altura * PROPORCAO_ESCURO,
          background: "var(--d-acento)",
          WebkitMaskImage: mascara,
          maskImage: mascara,
          WebkitMaskSize: "contain",
          maskSize: "contain",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "center",
          maskPosition: "center",
        }}
      />
    )
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src="/marca/welltrainer-claro.svg"
      alt="Welltrainer"
      height={altura}
      style={{ height: altura, width: "auto" }}
      className={cn("block select-none", className)}
      draggable={false}
    />
  )
}

/**
 * Só o símbolo, na cor da marca de cada tema.
 *
 * **No escuro ele é lime**, e não branco: é a mesma decisão da arte completa (o
 * símbolo do logo escuro é lime), e o acento é o que faz a marca ser reconhecida.
 * Herdar a cor do texto deixava o símbolo branco na navegação recolhida, que é
 * uma terceira versão da marca que ninguém desenhou.
 *
 * `herdarCor` existe para o caso de ele ficar sobre um bloco de cor fixa (lime,
 * preto), onde a cor certa é a do texto daquele bloco.
 */
export function SimboloWelltrainer({
  tamanho = 28,
  herdarCor,
  className,
}: {
  tamanho?: number
  herdarCor?: boolean
  className?: string
}) {
  const tema = useTema()
  const cor = herdarCor ? "currentColor" : tema === "escuro" ? "var(--d-acento)" : "var(--d-texto)"

  return (
    <svg
      viewBox="0 0 109 55.71"
      width={tamanho}
      height={(tamanho * 55.71) / 109}
      fill={cor}
      role="img"
      aria-label="Welltrainer"
      className={cn("block shrink-0", className)}
    >
      <path d="M29.53,3.41c-1.08-2.1-3.23-3.41-5.59-3.41H.03S0,.02,0,.04c.73,1.43,22.36,43.55,22.36,43.55l.37.72s.01.02.02.03c1.06,1.73,2.98,2.89,5.17,2.89s4.11-1.16,5.17-2.89c0,0,0,0,0,0,.03-.06,8.46-16.45,8.73-16.96,0,0,0-.02,0-.03L29.53,3.41Z" />
      <path d="M55.06,8.7c-2.16,0-4.05,1.13-5.11,2.84,0,0,0,0,0,0l-.43.83s-7.44,14.51-7.68,14.97c0,0,0,.02,0,.02l12.8,24.93c1.07,2.09,3.23,3.41,5.58,3.41h22.64v-.02S60.31,11.78,60.3,11.76c-1.04-1.82-2.99-3.05-5.24-3.05Z" />
      <path d="M101.19,0h-17.52s-.02,0-.02.01c-.31.6-9.94,19.37-9.95,19.38-.43.96-.61,2.05-.47,3.2.36,3.04,3.06,5.26,6.13,5.26h17.8l9.62-18.73c2.14-4.17-.89-9.13-5.57-9.13Z" />
    </svg>
  )
}
